import {
  Box,
  Chip,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import ReplayIcon from '@mui/icons-material/Replay';
import Forward10Icon from '@mui/icons-material/Forward10';
import { formatVideoTime } from '@/types';
import { proModeSurfaces } from '@/utils/themeSurfaces';

const SPEEDS = [0.25, 0.5, 1, 1.5, 2] as const;

interface Props {
  currentTime: number;
  videoDuration?: number;
  playbackRate: number;
  isPlaying?: boolean;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onPlaybackRateChange: (rate: number) => void;
  /** Jump back to the clip start (event timestamp) and play again. */
  onReplay?: () => void;
  replayAtSeconds?: number;
}

export default function ReviewVideoTransportBar({
  currentTime,
  videoDuration = 0,
  playbackRate,
  isPlaying = false,
  onTogglePlay,
  onSeek,
  onPlaybackRateChange,
  onReplay,
  replayAtSeconds,
}: Props) {
  const theme = useTheme();
  const surfaces = proModeSurfaces(theme);
  const isDark = surfaces.isDark;
  const hasDuration = videoDuration > 0;
  const maxTime = hasDuration ? videoDuration : Math.max(currentTime, 1);

  const transportBtnSx = {
    color: isDark ? 'rgba(255,255,255,0.92)' : theme.palette.text.primary,
    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : theme.palette.common.white,
    border: '1px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.12)' : theme.palette.divider,
    boxShadow: isDark ? 'none' : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
    '&:hover': {
      bgcolor: isDark ? 'rgba(255,255,255,0.14)' : alpha(theme.palette.secondary.main, 0.1),
      borderColor: isDark ? 'rgba(255,255,255,0.22)' : alpha(theme.palette.secondary.main, 0.45),
    },
  };

  const playBtnSx = {
    ...transportBtnSx,
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    borderColor: alpha(theme.palette.secondary.dark, isDark ? 0.5 : 0.35),
    boxShadow: isDark ? 'none' : `0 1px 3px ${alpha(theme.palette.secondary.dark, 0.25)}`,
    '&:hover': {
      bgcolor: theme.palette.secondary.dark,
      color: theme.palette.getContrastText(theme.palette.secondary.dark),
      borderColor: theme.palette.secondary.dark,
    },
  };

  const seekBy = (delta: number) => {
    const next = Math.max(0, Math.min(hasDuration ? videoDuration : Infinity, currentTime + delta));
    onSeek(next);
  };

  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 1, sm: 1.25 },
        borderTop: '1px solid',
        borderColor: surfaces.border,
        bgcolor: surfaces.timelineBg,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.65)' : 'text.secondary',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 88,
              flexShrink: 0,
            }}
          >
            {formatVideoTime(currentTime)} / {hasDuration ? formatVideoTime(videoDuration) : '—'}
          </Typography>
          <Slider
            size="small"
            value={Math.min(currentTime, maxTime)}
            min={0}
            max={maxTime}
            step={0.1}
            onChange={(_, value) => onSeek(Array.isArray(value) ? value[0] : value)}
            sx={{
              flex: 1,
              mx: 0.5,
              color: 'secondary.main',
              ...(isDark
                ? {
                    '& .MuiSlider-rail': { opacity: 0.35, bgcolor: 'rgba(255,255,255,0.2)' },
                    '& .MuiSlider-track': { border: 'none' },
                  }
                : {}),
            }}
            disabled={!hasDuration}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="後退 10 秒">
              <IconButton size="small" sx={transportBtnSx} onClick={() => seekBy(-10)} aria-label="後退 10 秒">
                <Replay10Icon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isPlaying ? '暫停' : '播放'}>
              <IconButton size="small" sx={playBtnSx} onClick={onTogglePlay} aria-label="播放/暫停">
                {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="前進 10 秒">
              <IconButton size="small" sx={transportBtnSx} onClick={() => seekBy(10)} aria-label="前進 10 秒">
                <Forward10Icon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onReplay && (
              <Tooltip
                title={
                  replayAtSeconds != null
                    ? `重播片段（${formatVideoTime(replayAtSeconds)}）`
                    : '重播片段'
                }
              >
                <IconButton size="small" sx={transportBtnSx} onClick={onReplay} aria-label="重播片段">
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
            {SPEEDS.map((speed) => (
              <Chip
                key={speed}
                label={`${speed}x`}
                size="small"
                variant={playbackRate === speed ? 'filled' : 'outlined'}
                color={playbackRate === speed ? 'secondary' : 'default'}
                onClick={() => onPlaybackRateChange(speed)}
                sx={{
                  cursor: 'pointer',
                  height: 24,
                  fontSize: '0.72rem',
                  fontWeight: playbackRate === speed ? 700 : 500,
                  ...(isDark && playbackRate !== speed
                    ? { borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)' }
                    : {}),
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
